import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { types as sdkTypes } from '../../../util/sdkLoader';
import { formatMoney } from '../../../util/currency';
import { listingUnitLabel } from '../../../util/editListingHelpers';
import { LISTING_STATE_CLOSED } from '../../../util/types';
import { useIntl } from '../../../util/reactIntl';
import { parse, stringify } from '../../../util/urlHelpers';
import useViewport from '../../../hooks/useViewport';

import css from './MobileBookingBar.module.css';

const { Money } = sdkTypes;

// All-in customer fee multiplier — matches OrderPanel.PriceMaybe (the
// inline desktop price). If we ever change the fee, both have to move
// together; if that becomes a recurring source of drift, extract a
// shared helper.
const ALL_IN_MULTIPLIER = 1.15;

// visualViewport-based heuristic: if the visual viewport is more than
// this many pixels shorter than the layout viewport, the soft keyboard
// is almost certainly up. 150px is enough to ignore browser-chrome
// shrinking on scroll while catching every common Android/iOS keyboard.
const KEYBOARD_THRESHOLD_PX = 150;

const formatTierPrice = (tier, currency, intl) => {
  if (!tier || typeof tier.pricePerDay !== 'number' || tier.pricePerDay <= 0) {
    return null;
  }
  const allInAmount = Math.round(tier.pricePerDay * ALL_IN_MULTIPLIER);
  const money = new Money(allInAmount, currency);
  try {
    const formatted = formatMoney(intl, money);
    // Strip ".00" cents to match the rest of the marketplace's price display.
    return typeof formatted === 'string' ? formatted.replace(/\.00$/, '') : formatted;
  } catch (e) {
    return null;
  }
};

/**
 * Sticky bottom CTA bar for the listing detail page on mobile.
 *
 * Replaces the inline Book CTA on mobile and consolidates booking actions
 * into a single, always-visible affordance. Tier-aware: when the user
 * picks a tier in the in-page tier card, the price here updates in real
 * time. Hides automatically when the soft keyboard is up so it doesn't
 * cover date / address inputs.
 *
 * Visibility rules:
 *   - Hidden on desktop (≥1024px) via CSS.
 *   - Hidden when the user is the listing's owner (no booking yourself).
 *   - When the listing is closed: shows a disabled "Listing closed" state.
 *   - When the soft keyboard is up: slides off-screen via transform.
 */
const MobileBookingBar = ({
  listing,
  selectedTierKey,
  marketplaceCurrency,
  isOwnListing,
  // Stream 3 PR-3: when provided AND the listing is in quote mode,
  // tapping the CTA invokes this callback (typically opens a
  // bottom-sheet quote-request modal) instead of pushing
  // ?orderOpen=true to the URL. Fixed-mode listings ignore this prop
  // and continue to use the URL-param-driven OrderPanel modal.
  onCustomQuoteClick,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const { isMobile } = useViewport();

  const publicData = listing?.attributes?.publicData || {};
  const listingState = listing?.attributes?.state;
  const isClosed = listingState === LISTING_STATE_CLOSED;
  // Quote-mode listings (publicData.pricingMode === 'quote') route the
  // mobile CTA into the Request-Quote flow instead of the Booking
  // flow. The desktop OrderPanel already branches on transaction
  // process; the mobile sticky bar needs the same logic. Default-on-
  // read for existing listings without pricingMode → 'fixed'.
  const isQuoteMode = publicData.pricingMode === 'quote';
  // Canonical price quantifier — same mapping every price surface uses.
  const unitLabel = listingUnitLabel(publicData);

  const tiers = publicData.tiers || null;
  const tierKeys = tiers ? Object.keys(tiers) : [];
  const hasTiers = tierKeys.length > 0;
  const effectiveTierKey = selectedTierKey || (hasTiers ? tierKeys[0] : null);
  const effectiveTier = effectiveTierKey ? tiers[effectiveTierKey] : null;

  // Price source priority:
  //   1. Active tier (fixed-price listings with multi-tier or default Basic).
  //   2. Listing's top-level price attribute (quote-mode listings,
  //      where F1 set this from publicData.startingPrice).
  // Both apply the 1.15 customer markup so the mobile bar matches
  // listing card / OrderPanel pricing.
  const formattedPrice = (() => {
    if (effectiveTier) {
      return formatTierPrice(effectiveTier, marketplaceCurrency, intl);
    }
    const priceAttr = listing?.attributes?.price;
    if (priceAttr && Number.isFinite(priceAttr.amount) && priceAttr.amount > 0) {
      const allInAmount = Math.round(priceAttr.amount * ALL_IN_MULTIPLIER);
      const money = new Money(allInAmount, priceAttr.currency || marketplaceCurrency);
      try {
        const formatted = formatMoney(intl, money);
        return typeof formatted === 'string' ? formatted.replace(/\.00$/, '') : formatted;
      } catch (e) {
        return null;
      }
    }
    return null;
  })();

  // Only label the tier when there's a meaningful choice to disambiguate
  // (multi-tier listings). Single-tier listings just show the price.
  const tierLabel = tierKeys.length > 1 ? effectiveTier?.name : null;

  // Soft-keyboard detection (cross-platform, design §11.2). Uses the
  // visualViewport API exclusively — the Web Platform standard, supported
  // on iOS Safari 13+ and all current Android browsers. No UA sniffing.
  // Older browsers fall back to keyboard-up never being detected, which
  // means the bar stays visible — acceptable graceful degradation.
  const [isKeyboardUp, setIsKeyboardUp] = useState(false);
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      const heightDelta = window.innerHeight - vv.height;
      setIsKeyboardUp(heightDelta > KEYBOARD_THRESHOLD_PX);
    };
    onResize();
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, [isMobile]);

  // Don't render at all on desktop or for the owner — desktop has its
  // own inline CTA, and owners can't book their own listing.
  if (!isMobile || isOwnListing) return null;

  // Click handler:
  //   - Closed listings: scroll up to surface the closure notice.
  //   - Quote-mode listings with onCustomQuoteClick: invoke the
  //     callback (page opens a bottom-sheet quote-request modal).
  //   - Everything else (fixed-mode): push ?orderOpen=true to the
  //     URL — OrderPanel's ModalInMobile listens for that.
  const handleClick = () => {
    if (isClosed) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      return;
    }
    if (isQuoteMode && typeof onCustomQuoteClick === 'function') {
      onCustomQuoteClick();
      return;
    }
    // Match OrderPanel.openOrderModal verbatim (boolean, not "true" string)
    // so the param shape stays consistent across the codebase.
    const search = `?${stringify({ ...parse(location.search), orderOpen: true })}`;
    history.push(`${location.pathname}${search}${location.hash}`);
  };

  const rootClasses = classNames(css.root, {
    [css.hidden]: isKeyboardUp,
  });

  return (
    <div className={rootClasses} role="region" aria-label="Booking">
      <div className={css.row}>
        <div className={css.priceBlock}>
          {formattedPrice ? (
            <>
              {/* Quote-mode listings show the floor price with "from"
                  rather than a per-unit suffix — the price is the
                  starting point of a custom quote, not a day-rate. */}
              {isQuoteMode ? (
                <span className={css.perUnit}>from </span>
              ) : null}
              <span className={css.priceValue}>{formattedPrice}</span>
              {unitLabel ? (
                <span className={css.perUnit}> {unitLabel}</span>
              ) : null}
              {tierLabel ? (
                <span className={css.tierLabel}>· {tierLabel}</span>
              ) : null}
            </>
          ) : (
            <span className={css.priceMissing}>Price unavailable</span>
          )}
        </div>
        <button
          type="button"
          className={classNames(css.cta, { [css.ctaDisabled]: isClosed })}
          onClick={handleClick}
          disabled={isClosed}
        >
          {isClosed
            ? 'Listing closed'
            : isQuoteMode
            ? 'Request a quote'
            : 'Request to book'}
        </button>
      </div>
      {!isClosed ? (
        <p className={css.reassurance}>
          You'll only be charged once your booking is confirmed.
        </p>
      ) : null}
    </div>
  );
};

export default MobileBookingBar;
